import axios, { AxiosInstance, AxiosResponse } from 'axios';
// 7721

// Resolve base API URL with the following precedence:
// 1) REACT_APP_API_URL (explicit override)
// 2) Development: localhost:8080
// 3) Production: relative /api (works on any domain since nginx proxies to backend)
export const resolvedBaseURL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'development'
    ? 'http://localhost:8080/api'
    : '/api');

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: resolvedBaseURL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

let activeRequestWorkspaceId = '';
export const setActiveRequestWorkspace = (workspaceId: string): void => { activeRequestWorkspaceId = workspaceId; };
const requestWorkspace = (config: { headers?: unknown } | undefined): string => {
  const headers = config?.headers as { get?: (name: string) => unknown; [key: string]: unknown } | undefined;
  const value = headers?.get?.('X-Workspace-ID') ?? headers?.['X-Workspace-ID'];
  return typeof value === 'string' ? value : '';
};
const rejectStaleWorkspace = (config: { headers?: unknown } | undefined): void => {
  const captured = requestWorkspace(config);
  if (captured && captured !== activeRequestWorkspaceId) throw new DOMException('The active workspace changed.', 'AbortError');
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Attach workspace header
    const workspaceId = localStorage.getItem('currentWorkspaceId');
    if (workspaceId && !config.headers['X-Workspace-ID']) {
      config.headers['X-Workspace-ID'] = workspaceId;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response: AxiosResponse) => {
    rejectStaleWorkspace(response.config);
    return response;
  },
  (error) => {
    rejectStaleWorkspace(error?.config);
    if (error.response?.status === 403 && error.response?.data?.error?.code === 'forbidden') {
      window.dispatchEvent(new CustomEvent('v2-forbidden', { detail: { workspaceId: requestWorkspace(error.config), url: String(error.config?.url || '') } }));
    }
    // A rejected login belongs to the login form; redirecting reloads the page
    // before it can show the reason the new account could not sign in.
    if (error.response?.status === 401 && error.config?.url !== '/auth/login') {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('workspaces');
      localStorage.removeItem('currentWorkspaceId');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;

// Resolve a possibly-relative asset path against the backend origin derived
// from the configured API base URL.
export const resolveAssetURL = (path?: string | null): string | undefined => {
  if (!path) return undefined;
  try {
    // If already absolute URL, return as-is
    if (/^https?:\/\//i.test(path)) return path;
    const base = new URL(resolvedBaseURL);
    const origin = `${base.protocol}//${base.host}`;
    if (path.startsWith('/')) return origin + path;
    return `${origin}/${path}`;
  } catch {
    return path;
  }
};

// WebSocket client helper
export const createRealtimeSocket = (tokenOverride?: string): WebSocket | null => {
  // Build WS URL from API base URL
  try {
    const base = new URL(resolvedBaseURL);
    const protocol = base.protocol === 'https:' ? 'wss:' : 'ws:';
    const token = tokenOverride ?? localStorage.getItem('token');
    const wsURL = `${protocol}//${base.host}/api/ws${token ? `?token=${encodeURIComponent(token)}` : ''}`;
    return new WebSocket(wsURL);
  } catch {
    return null;
  }
};

export const createWorkspaceRealtimeSocket = (workspaceId: string, token: string): WebSocket | null => {
  try {
    const base = new URL(resolvedBaseURL, window.location.origin);
    const protocol = base.protocol === 'https:' ? 'wss:' : 'ws:';
    const query = new URLSearchParams({ workspace_id: workspaceId });
    return new WebSocket(`${protocol}//${base.host}/api/v2/ws?${query}`, ['football-v2', `bearer.${token}`]);
  } catch {
    return null;
  }
};
